/**
 * Sélection d'un point sur la carte.
 *
 * Deux modes, tous deux consommant le tap suivant :
 *
 *  - **sur une trace** : le tap est projeté sur la géométrie et renvoie les
 *    points kilométriques candidats — plusieurs si la trace repasse au même
 *    endroit. Un tap hors trace vaut abandon, comme pour le déplacement d'un
 *    marqueur.
 *  - **libre** : le tap renvoie ses coordonnées telles quelles, pour un point
 *    de passage qui n'a aucune raison d'être sur le parcours.
 *
 * Ces modes priment sur le clic ordinaire et neutralisent l'appui long : le
 * même geste servirait sinon deux intentions contradictoires.
 */

import type { GpxTrack, DistanceCandidate } from '../core/track'
import { metersPerPixel } from '../core/geo'
import type { Map as MapLibreMap } from 'maplibre-gl'
import type { Shell } from './shell'

/** Même tolérance que l'appui long : viser une trace au doigt demande de la marge. */
const PICK_TOLERANCE_PX = 30

interface TrackPick {
  readonly kind: 'track'
  readonly track: GpxTrack
  readonly onPick: (candidates: readonly DistanceCandidate[]) => void
  readonly dismiss: () => void
}

interface MapPick {
  readonly kind: 'map'
  readonly onPick: (position: { lat: number; lon: number }) => void
  readonly dismiss: () => void
}

type PendingPick = TrackPick | MapPick

export interface MapPickDeps {
  readonly map: MapLibreMap
  readonly shell: Shell
  readonly cancelLabel: string
}

export class MapPickController {
  #pending: PendingPick | null = null

  constructor(private readonly deps: MapPickDeps) {}

  get isPicking(): boolean {
    return this.#pending !== null
  }

  /**
   * Attend un tap sur la trace donnée.
   *
   * Le rappel reçoit une liste vide si le tap tombe à côté, ce que l'appelant
   * traite comme un abandon.
   */
  startTrackPick(
    track: GpxTrack,
    hint: string,
    onPick: (candidates: readonly DistanceCandidate[]) => void,
  ): void {
    this.cancel()

    const dismiss = this.deps.shell.showSnackbar(hint, {
      label: this.deps.cancelLabel,
      onClick: () => this.cancel(),
    })

    this.#pending = { kind: 'track', track, onPick, dismiss }
  }

  /** Attend un tap n'importe où sur la carte. */
  startMapPick(
    hint: string,
    onPick: (position: { lat: number; lon: number }) => void,
  ): void {
    this.cancel()

    const dismiss = this.deps.shell.showSnackbar(hint, {
      label: this.deps.cancelLabel,
      onClick: () => this.cancel(),
    })

    this.#pending = { kind: 'map', onPick, dismiss }
  }

  cancel(): void {
    this.#pending?.dismiss()
    this.#pending = null
  }

  /** Abandonne la sélection si la trace visée disparaît. */
  releaseTrack(trackId: string): void {
    if (this.#pending?.kind === 'track' && this.#pending.track.id === trackId) {
      this.cancel()
    }
  }

  /**
   * Traite un clic.
   *
   * @returns `true` si le clic a été consommé.
   */
  handleMapClick(lat: number, lon: number): boolean {
    const pending = this.#pending
    if (pending === null) return false

    this.#pending = null
    pending.dismiss()

    if (pending.kind === 'map') {
      pending.onPick({ lat, lon })
      return true
    }

    const tolerance =
      metersPerPixel(this.deps.map.getZoom(), lat) * PICK_TOLERANCE_PX

    pending.onPick(pending.track.projectPointAll(lat, lon, tolerance))
    return true
  }
}
